import React, { useState, useRef, useEffect, useCallback } from "react";
import frame1 from "../../assets/images/frame1.png";
import frame2 from "../../assets/images/frame2.png";
import frame3 from "../../assets/images/frame3.png";

// ✅ Stable reference — defined outside component, never recreated on re-render
const sampleFrames = [frame1, frame2, frame3];

const Capture = () => {
  const [frameImage, setFrameImage] = useState(null);
  const [userPhoto, setUserPhoto] = useState(null);
  const [photoPosition, setPhotoPosition] = useState({ x: 0, y: 0 });
  const [photoScale, setPhotoScale] = useState(2.5);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [frameDimensions, setFrameDimensions] = useState({ width: 0, height: 0 });
  const [photoDimensions, setPhotoDimensions] = useState({ width: 0, height: 0 });
  const [previewScale, setPreviewScale] = useState(1);
  const [selectedSample, setSelectedSample] = useState(null);

  const canvasRef = useRef(null);
  const previewRef = useRef(null);
  const fileInputRef = useRef(null);

  // ✅ Shared helper — calculates preview scale from frame width
  const calcPreviewScale = useCallback((frameWidth) => {
    const maxWidth = Math.min(500, window.innerWidth - 40);
    return maxWidth / frameWidth;
  }, []);

  // ✅ Wrapped in useCallback — stable reference for event listeners
  const handleFrameUpload = useCallback((event) => {
    const file = event.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const img = new Image();
      img.onload = () => {
        setFrameDimensions({ width: img.width, height: img.height });
        setFrameImage(reader.result);
        setSelectedSample(null);
        setPreviewScale(calcPreviewScale(img.width));
      };
      img.src = reader.result;
    };
    reader.readAsDataURL(file);
  }, [calcPreviewScale]);

  const handleSampleFrameClick = useCallback((frameSrc, index) => {
    const img = new Image();
    img.onload = () => {
      setFrameDimensions({ width: img.width, height: img.height });
      setFrameImage(frameSrc);
      setSelectedSample(index);
      setPreviewScale(calcPreviewScale(img.width));
    };
    img.src = frameSrc;
  }, [calcPreviewScale]);

  const handleAddCustomFrame = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const handlePhotoUpload = useCallback((event) => {
    const file = event.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const img = new Image();
      img.onload = () => {
        setPhotoDimensions({ width: img.width, height: img.height });
        setUserPhoto(reader.result);
        setPhotoPosition({ x: 0, y: 0 });
        setPhotoScale(2.5);
      };
      img.src = reader.result;
    };
    reader.readAsDataURL(file);
  }, []);

  const handleMouseDown = useCallback((e) => {
    if (!userPhoto) return;
    e.preventDefault();
    const rect = previewRef.current.getBoundingClientRect();
    const clientX = e.clientX ?? e.touches?.[0]?.clientX;
    const clientY = e.clientY ?? e.touches?.[0]?.clientY;
    setIsDragging(true);
    setDragStart({
      x: clientX - rect.left - photoPosition.x,
      y: clientY - rect.top - photoPosition.y,
    });
  }, [userPhoto, photoPosition]);

  const handleMouseMove = useCallback((e) => {
    if (!isDragging || !previewRef.current) return;
    e.preventDefault();
    const rect = previewRef.current.getBoundingClientRect();
    const clientX = e.clientX ?? e.touches?.[0]?.clientX;
    const clientY = e.clientY ?? e.touches?.[0]?.clientY;
    setPhotoPosition({
      x: clientX - rect.left - dragStart.x,
      y: clientY - rect.top - dragStart.y,
    });
  }, [isDragging, dragStart]);

  const handleMouseUp = useCallback(() => setIsDragging(false), []);

  const handleZoomIn = useCallback(() => {
    setPhotoScale((prev) => Math.min(prev + 0.2, 10));
  }, []);

  const handleZoomOut = useCallback(() => {
    setPhotoScale((prev) => Math.max(prev - 0.2, 0.3));
  }, []);

  const handleScaleChange = useCallback((e) => {
    setPhotoScale(parseFloat(e.target.value));
  }, []);

  const resetPosition = useCallback(() => {
    setPhotoPosition({ x: 0, y: 0 });
    setPhotoScale(2.5);
  }, []);

  // ✅ downloadMemory — revokeObjectURL moved inside onload to prevent memory leaks
  const downloadMemory = useCallback(() => {
    if (!userPhoto || !frameImage) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    const photo = new Image();
    const frame = new Image();
    let loaded = 0;

    const onBothLoaded = () => {
      loaded++;
      if (loaded < 2) return;

      canvas.width = frameDimensions.width;
      canvas.height = frameDimensions.height;
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      const actualPhotoX = photoPosition.x / previewScale;
      const actualPhotoY = photoPosition.y / previewScale;
      const actualPhotoWidth = photoDimensions.width * photoScale;
      const actualPhotoHeight = photoDimensions.height * photoScale;

      ctx.drawImage(photo, actualPhotoX, actualPhotoY, actualPhotoWidth, actualPhotoHeight);
      ctx.drawImage(frame, 0, 0, frameDimensions.width, frameDimensions.height);

      canvas.toBlob((blob) => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = "luster-memory-for-you.png";
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        // ✅ Revoke after click to free memory
        setTimeout(() => URL.revokeObjectURL(url), 100);
      }, "image/png");
    };

    photo.onload = onBothLoaded;
    frame.onload = onBothLoaded;
    photo.src = userPhoto;
    frame.src = frameImage;
  }, [userPhoto, frameImage, frameDimensions, photoDimensions, photoPosition, photoScale, previewScale]);

  // Drag event listeners
  useEffect(() => {
    if (!isDragging) return;
    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", handleMouseUp);
    window.addEventListener("touchmove", handleMouseMove, { passive: false });
    window.addEventListener("touchend", handleMouseUp);
    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
      window.removeEventListener("touchmove", handleMouseMove);
      window.removeEventListener("touchend", handleMouseUp);
    };
  }, [isDragging, handleMouseMove, handleMouseUp]);

  // ✅ Debounced resize handler — prevents excessive recalculations
  useEffect(() => {
    if (!frameDimensions.width) return;
    let timer;
    const handleResize = () => {
      clearTimeout(timer);
      timer = setTimeout(() => {
        setPreviewScale(calcPreviewScale(frameDimensions.width));
      }, 200);
    };
    window.addEventListener("resize", handleResize);
    return () => {
      clearTimeout(timer);
      window.removeEventListener("resize", handleResize);
    };
  }, [frameDimensions.width, calcPreviewScale]);

  return (
    <div className="w-full max-w-[1200px] mx-auto px-4 py-8 text-center">
      <p className="text-[#76cfff] font-poppins tracking-[4px] uppercase font-bold text-sm md:text-[16px] leading-6 mb-4">
        Try your frame now
      </p>

      <h2 className="text-white font-poppins uppercase font-semibold text-[60px] md:text-[120px] lg:text-[160px] leading-[1.1] text-center mb-10">
        Upload a Frame+Photo
      </h2>

      {/* Sample Frame Cards */}
      <div className="flex gap-3 md:gap-4 mb-8 justify-center flex-wrap" data-aos="fade-up">
        <p className="text-[#76cfff] font-poppins text-sm md:text-base font-medium w-full mb-2">
          Choose a Sample Frame or Upload Your Own:
        </p>

        {sampleFrames.map((frameSrc, index) => (
          <div
            key={index}
            onClick={() => handleSampleFrameClick(frameSrc, index)}
            className={`w-20 h-20 md:w-28 md:h-28 rounded-lg border-2 cursor-pointer transition-all hover:scale-105 ${
              selectedSample === index
                ? "border-[#76cfff] shadow-lg shadow-[#76cfff]/50"
                : "border-gray-600"
            }`}
          >
            {/* ✅ Thumbnails — lazy loaded with fixed dimensions to prevent layout shift */}
            <img
              src={frameSrc}
              alt={`Sample Frame ${index + 1}`}
              loading="lazy"
              decoding="async"
              fetchpriority="low"
              width={112}
              height={112}
              className="w-full h-full object-cover rounded-lg"
            />
          </div>
        ))}

        {/* Add Custom Frame Card */}
        <div
          onClick={handleAddCustomFrame}
          className={`w-20 h-20 md:w-28 md:h-28 rounded-lg border-2 border-dashed cursor-pointer transition-all hover:scale-105 hover:bg-[#76cfff]/10 flex items-center justify-center ${
            selectedSample === null && frameImage
              ? "border-[#76cfff] bg-[#76cfff]/10"
              : "border-[#76cfff]"
          }`}
        >
          <svg
            className="w-10 h-10 md:w-12 md:h-12 text-[#76cfff]"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            aria-hidden="true"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 md:gap-8 mb-8">
        {/* Upload Section */}
        <div className="space-y-4 md:space-y-6 order-2 lg:order-1">
          {/* Hidden file input for custom frame */}
          <input
            type="file"
            ref={fileInputRef}
            accept="image/*"
            onChange={handleFrameUpload}
            className="hidden"
          />

          <div>
            <h3 className="text-[#76cfff] font-poppins text-base md:text-[18px] font-medium mb-3">
              Upload Your Photo Here
            </h3>
            <input
              type="file"
              accept="image/*"
              onChange={handlePhotoUpload}
              className="block mx-auto bg-[#76cfff] text-black py-2 px-4 text-sm rounded hover:bg-[#4ab1ff] cursor-pointer transition w-full max-w-xs"
            />
          </div>

          {userPhoto && frameImage && (
            <div className="bg-gray-800 p-4 rounded-lg space-y-4">
              <h3 className="text-white font-poppins text-sm md:text-[16px] font-medium">
                Adjust Your Photo
              </h3>
              <div className="space-y-2">
                <label className="text-[#76cfff] text-xs md:text-sm block">
                  Zoom: {photoScale.toFixed(1)}x
                </label>
                <input
                  type="range"
                  min="0.3"
                  max="10"
                  step="0.1"
                  value={photoScale}
                  onChange={handleScaleChange}
                  className="w-full accent-[#76cfff]"
                />
              </div>
              <div className="flex gap-2 justify-center flex-wrap">
                <button onClick={handleZoomOut} className="bg-[#76cfff] text-black py-2 px-3 md:px-4 text-xs md:text-sm rounded hover:bg-[#4ab1ff] transition">
                  Zoom Out -
                </button>
                <button onClick={handleZoomIn} className="bg-[#76cfff] text-black py-2 px-3 md:px-4 text-xs md:text-sm rounded hover:bg-[#4ab1ff] transition">
                  Zoom In +
                </button>
                <button onClick={resetPosition} className="bg-gray-600 text-white py-2 px-3 md:px-4 text-xs md:text-sm rounded hover:bg-gray-700 transition">
                  Reset
                </button>
              </div>
              <p className="text-gray-400 text-xs">💡 Drag the photo to reposition it</p>
            </div>
          )}

          {userPhoto && frameImage && (
            <button
              onClick={downloadMemory}
              className="bg-[#76cfff] text-black py-3 px-6 md:px-8 text-sm md:text-base rounded hover:bg-[#4ab1ff] transition font-semibold uppercase tracking-wide w-full max-w-xs mx-auto block"
            >
              Download Your Memory
            </button>
          )}
        </div>

        {/* Preview Section */}
        <div className="order-1 lg:order-2">
          <h3 className="text-white font-poppins text-base md:text-[18px] font-medium mb-4">
            Preview
          </h3>
          <div className="flex justify-center">
            {frameImage || userPhoto ? (
              <div
                ref={previewRef}
                className="relative border-2 border-[#76cfff] rounded-lg overflow-hidden bg-gray-900 touch-none"
                style={{
                  width: `${frameDimensions.width * previewScale}px`,
                  height: `${frameDimensions.height * previewScale}px`,
                  maxWidth: "100%",
                }}
              >
                {userPhoto && (
                  <img
                    src={userPhoto}
                    alt="Uploaded preview"
                    className="absolute cursor-move select-none"
                    style={{
                      transform: `translate(${photoPosition.x}px, ${photoPosition.y}px) scale(${photoScale})`,
                      transformOrigin: "top left",
                      maxWidth: "none",
                      width: `${photoDimensions.width * previewScale}px`,
                      height: `${photoDimensions.height * previewScale}px`,
                    }}
                    onMouseDown={handleMouseDown}
                    onTouchStart={handleMouseDown}
                    draggable={false}
                  />
                )}
                {frameImage && (
                  <img
                    src={frameImage}
                    alt="Frame overlay"
                    className="absolute top-0 left-0 w-full h-full pointer-events-none select-none"
                    draggable={false}
                  />
                )}
              </div>
            ) : (
              <div className="flex items-center justify-center h-64 md:h-96 w-full border-2 border-gray-700 rounded-lg text-gray-500 text-sm md:text-base">
                Upload images to preview
              </div>
            )}
          </div>
        </div>
      </div>

      <canvas ref={canvasRef} className="hidden" />
    </div>
  );
};

export default Capture;